import { cva } from 'class-variance-authority';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * A utility function to merge Tailwind CSS classes.
 * It uses `clsx` to conditionally apply classes and `tailwind-merge` to resolve conflicts.
 * @param inputs - A list of class values to merge.
 * @returns A string of merged class names.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * A re-export of the `cva` (class-variance-authority) function.
 * This allows for creating type-safe variant classes.
 */
export const variants = cva;

/**
 * A boolean flag that is true if the application is running in a production environment.
 * It checks the `NEXT_PUBLIC_APP_VERSION` environment variable.
 */
export const isProd = process.env.NEXT_PUBLIC_APP_VERSION !== undefined && process.env.NEXT_PUBLIC_APP_VERSION !== 'preview';

/**
 * A boolean flag that is true if the application is running in a preview environment.
 * It checks the `NEXT_PUBLIC_APP_VERSION` environment variable.
 */
export const isPreview = process.env.NEXT_PUBLIC_APP_VERSION === 'preview';

/**
 * A boolean flag that is true if the application is running in a development environment.
 * It checks the `NEXT_PUBLIC_APP_VERSION` environment variable.
 */
export const isDev = process.env.NEXT_PUBLIC_APP_VERSION === undefined;

/**
 * Parses a Datadog sample rate (0-100) from an environment variable value.
 * Values outside 0-100 are clamped. A missing or non-numeric value returns `fallback`.
 * Pass `process.env.NEXT_PUBLIC_*` directly so Next.js can inline it.
 */
export function parseSampleRate(raw: string | undefined, fallback: number): number {
  if (raw === undefined || raw.trim() === '') return fallback;
  const value = Number(raw);
  if (!Number.isFinite(value)) return fallback;
  return Math.min(100, Math.max(0, value));
}
